'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>('light')
  const [isClient, setIsClient] = useState(false)

  // Initialize theme from localStorage on mount
  useEffect(() => {
    setIsClient(true)
    if (typeof window !== 'undefined') {
      const saved = window.localStorage.getItem('theme')
      if (saved === 'dark' || saved === 'light') {
        setTheme(saved)
        // Set initial data-theme attribute
        document.documentElement.setAttribute('data-theme', saved)
      } else {
        // Set default theme
        document.documentElement.setAttribute('data-theme', 'light')
      }
    }
  }, [])

  // Save theme to localStorage whenever it changes
  useEffect(() => {
    if (isClient && typeof window !== 'undefined') {
      window.localStorage.setItem('theme', theme)
      // Set data-theme attribute for CSS variables
      document.documentElement.setAttribute('data-theme', theme)
    }
  }, [theme, isClient])

  const value = {
    theme,
    setTheme
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
